import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';

const UpdateEmail: React.FC = () => {
  const navigate = useNavigate();
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setCurrentEmail(data.user.email);
          if (data.user.pending_email) {
            setPendingEmail(data.user.pending_email);
          }
        } else {
          navigate('/login');
        }
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes('@')) {
      Swal.fire({
        title: 'Error',
        text: 'Enter a valid email',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded'
        },
        buttonsStyling: false
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/update-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ newEmail }),
      });

      const data = await res.json();
      if (res.ok) {
        Swal.fire({
          title: 'Success',
          text: data.message,
          icon: 'success',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded'
          },
          buttonsStyling: false
        });
        setPendingEmail(newEmail);
        setNewEmail('');
      } else {
        Swal.fire({
          title: 'Error',
          text: data.error || 'Failed to update email',
          icon: 'error',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded'
          },
          buttonsStyling: false
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: 'Error',
        text: 'Something went wrong',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded'
        },
        buttonsStyling: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPending = async () => {
    const res = await fetch(`${API_BASE_URL}/auth/cancel-pending-email`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });

    const data = await res.json();
    if (res.ok) {
      setPendingEmail('');
      Swal.fire({
        title: 'Cleared',
        text: 'You can now enter a new email address.',
        icon: 'info',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded'
        },
        buttonsStyling: false
      });
    } else {
      Swal.fire({
        title: 'Error',
        text: data.error || 'Failed to clear pending email',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded'
        },
        buttonsStyling: false
      });
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-6 text-center text-indigo-700">Update Email</h2>

      <div className="mb-4">
        <label className="text-sm text-gray-600">Current Email</label>
        <input
          type="email"
          value={currentEmail}
          disabled
          className="w-full p-2 border bg-gray-100 rounded"
        />
      </div>

      {pendingEmail ? (
        <div className="text-center text-gray-700 space-y-4">
          <p>✅ You already requested to change your email to:</p>
          <p className="font-semibold text-indigo-600">{pendingEmail}</p>
          <p className="text-sm text-gray-500">
            Please check your inbox and confirm to complete the change.
          </p>

          <button
            onClick={handleCancelPending}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            ✏️ Change Another Email
          </button>
        </div>
      ) : (
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">New Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
          >
            {loading ? 'Updating...' : 'Update Email'}
          </button>
        </form>
      )}
    </div>
  );
};

export default UpdateEmail;
